import i18next from "i18next";
import { COUNTRY_DEFAULT_LOCALE } from "./countryDefaultLocale";
const isValidLocale = (locale) => {
    if (!locale)
        return false;
    try {
        const getCanonical = Intl.getCanonicalLocales;
        if (typeof getCanonical === "function") {
            getCanonical(locale);
            return true;
        }
        if (typeof Intl.Locale === "function") {
            new Intl.Locale(locale);
            return true;
        }
        // If neither API exists, we cannot validate, so assume valid to avoid false negatives
        return true;
    }
    catch (_a) {
        return false;
    }
};
const pickSupportedLocale = (candidates, fallback) => {
    const nonEmptyCandidates = candidates.filter(Boolean);
    const hasInvalid = nonEmptyCandidates.some((candidate) => !isValidLocale(candidate));
    if (hasInvalid)
        return fallback;
    const normalizedCandidates = nonEmptyCandidates.filter(isValidLocale);
    const supported = Intl.NumberFormat.supportedLocalesOf(normalizedCandidates);
    if (supported.length > 0)
        return supported[0];
    // supportedLocalesOf may be empty on some older engines, so try/catch as a fallback
    for (const locale of normalizedCandidates) {
        try {
            new Intl.NumberFormat(locale);
            return locale;
        }
        catch (_e) {
            // continue
        }
    }
    return fallback;
};
const resolveLocale = () => {
    var _a, _b, _c;
    const country = (_b = (typeof window !== "undefined"
        ? sessionStorage.getItem("country") || ((_a = window === null || window === void 0 ? void 0 : window.Shopify) === null || _a === void 0 ? void 0 : _a.country)
        : "")) !== null && _b !== void 0 ? _b : "";
    // start from explicit language stored by i18n to keep user preference, then attach region when possible
    const languageRaw = (typeof window !== "undefined" ? sessionStorage.getItem("i18nextLng") : "") ||
        i18next.language ||
        (typeof navigator !== "undefined" ? navigator.language : "") ||
        "en-US";
    // normalize language tags (handle underscores) to avoid falling back to en-US
    const normalizedLanguage = languageRaw.replace("_", "-");
    const [languageCode, languageRegionRaw] = normalizedLanguage.split("-");
    const languageRegion = (languageRegionRaw === null || languageRegionRaw === void 0 ? void 0 : languageRegionRaw.toUpperCase()) || "";
    const normalizedCountry = (country !== null && country !== void 0 ? country : "").toString().toUpperCase();
    const languageHasRegion = Boolean(languageRegion);
    const forcedLocale = normalizedCountry ? COUNTRY_DEFAULT_LOCALE[normalizedCountry] : "";
    // derive likely locale for the country using Intl.Locale maximize when available
    let likelyLocale = "";
    if (normalizedCountry && typeof Intl !== "undefined" && typeof Intl.Locale === "function") {
        try {
            const maximized = new Intl.Locale(`und-${normalizedCountry}`).maximize();
            likelyLocale = ((_c = maximized === null || maximized === void 0 ? void 0 : maximized.toString) === null || _c === void 0 ? void 0 : _c.call(maximized)) || "";
        }
        catch (_e) {
            // ignore
        }
    }
    const candidates = Array.from(new Set([
        forcedLocale,
        normalizedCountry ? `${languageCode.toLowerCase()}-${normalizedCountry}` : "",
        // avoid generating invalid language tags when language already has a region (e.g., en-CO-US)
        normalizedCountry && !languageHasRegion ? `${normalizedLanguage}-${normalizedCountry}` : "",
        likelyLocale,
        normalizedLanguage,
    ]));
    return pickSupportedLocale(candidates, "en-US");
};
const isValidCurrencyCode = (code) => {
    if (!code || typeof code !== "string")
        return false;
    // ISO 4217 currency codes are exactly 3 ASCII letters
    return /^[A-Z]{3}$/i.test(code.trim());
};
export const formatPrice = (price, currency) => {
    const integerPrice = Math.round(price);
    const safeCurrency = isValidCurrencyCode(currency) ? currency.trim().toUpperCase() : "USD";
    const formatter = new Intl.NumberFormat(resolveLocale(), {
        style: "currency",
        currency: safeCurrency,
        useGrouping: true,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });
    const normalizeNumerals = (value) => value
        .replace(/[٠-٩]/g, (digit) => String(digit.charCodeAt(0) - 0x0660))
        .replace(/[۰-۹]/g, (digit) => String(digit.charCodeAt(0) - 0x06f0))
        .replace(/[০-৯]/g, (digit) => String(digit.charCodeAt(0) - 0x09e6));
    const parts = formatter.formatToParts(integerPrice);
    let result = "";
    for (const part of parts) {
        if (part.type === "integer") {
            result += normalizeNumerals(part.value);
        }
        else if (part.type === "group") {
            // Space-based group separators (NO-BREAK SPACE U+00A0, NARROW NO-BREAK SPACE U+202F,
            // THIN SPACE U+2009) introduced by CLDR 42+ are invisible and get stripped by downstream
            // consumers. Normalize them to period which is the visible convention for affected locales.
            result += /[\s\u00A0\u202F\u2009]/.test(part.value) ? "." : part.value;
        }
    }
    return result;
};
